import {
  enabledAuthProviders,
  type AuthSession,
  type AuthSessionSeed,
  type EnabledAuthProviderKind,
} from "@/modules/auth/public";
import { loadPersistedAuthSession } from "@/auth/authSessionStorage";
import { type MePermissions, type UserRole } from "@/modules/auth/public";

export type AppLifecycleState = {
  startedAt: string;
  errorMessage: string | null;
};

export type AuthState =
  | {
      status: "signedOut";
      enabledProviders: readonly EnabledAuthProviderKind[];
      session: null;
    }
  | {
      status: "signedIn";
      enabledProviders: readonly EnabledAuthProviderKind[];
      session: AuthSession;
    };

export type CurrentUser = {
  uid: string;
  memberUuid: string | null;
  displayName: string;
  email: string | null;
  notificationEmail: string | null;
  role: UserRole;
  permissions: MePermissions | null;
};

export type CurrentUserProfile = {
  user: {
    uuid: string;
    display_name: string;
    email: string | null;
    notification_email: string | null;
  };
  member: {
    uuid: string;
  };
  // 단일 권한 등급(/v1/me tier).
  tier: UserRole;
  // 서버가 계산한 공유 삭제 권한이다. /v1/me 응답이 항상 포함한다.
  permissions: MePermissions;
};

export type VolatileAppData = {
  pageCache: Record<string, unknown>;
  transientSelections: Record<string, unknown>;
};

export type SharedPreferenceScope = {
  uid: string | null;
  storagePrefix: "hatchery-registry";
};

export type AppState = {
  lifecycle: AppLifecycleState;
  auth: AuthState;
  currentUser: CurrentUser | null;
  volatile: VolatileAppData;
  sharedPreferenceScope: SharedPreferenceScope;
};

export type InitializeAppStateOptions = {
  minimumSplashDurationMs?: number;
};

export type AppStateAction =
  | { type: "app/initializeStarted"; startedAt: string }
  | { type: "app/initializeSucceeded"; state: AppState }
  | { type: "app/initializeFailed"; errorMessage: string }
  | { type: "auth/sessionStarted"; session: AuthSession }
  | { type: "auth/currentUserLoaded"; profile: CurrentUserProfile }
  | { type: "auth/sessionEnded" }
  | { type: "volatile/patch"; value: Partial<VolatileAppData> }
  | { type: "volatile/reset" };

// 앱 초기화는 중복 복구를 막기 위해 한 번만 실행한다.
let initializationPromise: Promise<AppState> | null = null;

export function createInitialAppState(startedAt = new Date().toISOString()): AppState {
  return {
    lifecycle: {
      startedAt,
      errorMessage: null,
    },
    auth: createSignedOutAuthState(),
    currentUser: null,
    volatile: createEmptyVolatileAppData(),
    sharedPreferenceScope: createSharedPreferenceScope(null),
  };
}

export function appStateReducer(state: AppState, action: AppStateAction): AppState {
  switch (action.type) {
    case "app/initializeStarted":
      return createInitialAppState(action.startedAt);
    case "app/initializeSucceeded":
      return action.state;
    case "app/initializeFailed":
      return {
        ...state,
        lifecycle: {
          ...state.lifecycle,
          errorMessage: action.errorMessage,
        },
      };
    case "auth/sessionStarted":
      return createSignedInState(state, normalizeAuthSession(action.session));
    case "auth/currentUserLoaded":
      return state.auth.status === "signedIn"
        ? createSignedInState(
            state,
            updateAuthSessionFromProfile(state.auth.session, action.profile),
          )
        : state;
    case "auth/sessionEnded":
      return {
        ...state,
        auth: createSignedOutAuthState(),
        currentUser: null,
        volatile: createEmptyVolatileAppData(),
        sharedPreferenceScope: createSharedPreferenceScope(null),
      };
    case "volatile/patch":
      return {
        ...state,
        volatile: {
          ...state.volatile,
          ...action.value,
        },
      };
    case "volatile/reset":
      return {
        ...state,
        volatile: createEmptyVolatileAppData(),
      };
  }
}

export function initializeAppState({
  minimumSplashDurationMs = 350,
}: InitializeAppStateOptions = {}): Promise<AppState> {
  if (initializationPromise !== null) {
    return initializationPromise;
  }

  const startedAt = new Date().toISOString();
  const startedAtMs = Date.now();

  initializationPromise = (async () => {
    const baseState = createInitialAppState(startedAt);
    const restoredState = restoreSignedInState(baseState);
    await waitForMinimumSplashDuration(startedAtMs, minimumSplashDurationMs);

    return restoredState;
  })();

  return initializationPromise;
}

function createSignedOutAuthState(): AuthState {
  return {
    status: "signedOut",
    enabledProviders: enabledAuthProviders,
    session: null,
  };
}

function restoreSignedInState(base: AppState): AppState {
  const session = loadPersistedAuthSession();
  if (session === null) {
    return base;
  }

  return appStateReducer(base, { type: "auth/sessionStarted", session });
}

function createSignedInState(state: AppState, session: AuthSession): AppState {
  return {
    ...state,
    auth: {
      status: "signedIn",
      enabledProviders: enabledAuthProviders,
      session,
    },
    currentUser: createCurrentUser(session),
    sharedPreferenceScope: createSharedPreferenceScope(session.uid),
  };
}

export function updateAuthSessionFromProfile(
  session: AuthSessionSeed,
  profile: CurrentUserProfile,
): AuthSession {
  return {
    ...session,
    uid: profile.user.uuid,
    memberUuid: profile.member.uuid,
    displayName: profile.user.display_name,
    email: profile.user.email,
    notificationEmail: profile.user.notification_email,
    role: profile.tier,
    permissions: profile.permissions,
  };
}

export function normalizeAuthSession(session: AuthSession): AuthSession {
  return {
    ...session,
    memberUuid: session.memberUuid ?? null,
    permissions: session.permissions ?? null,
    notificationEmail: session.notificationEmail ?? null,
  };
}

function createCurrentUser(session: AuthSession): CurrentUser {
  return {
    uid: session.uid,
    memberUuid: session.memberUuid,
    displayName: session.displayName,
    email: session.email,
    notificationEmail: session.notificationEmail ?? null,
    role: session.role,
    permissions: session.permissions ?? null,
  };
}

function createEmptyVolatileAppData(): VolatileAppData {
  return {
    pageCache: {},
    transientSelections: {},
  };
}

function createSharedPreferenceScope(uid: string | null): SharedPreferenceScope {
  return {
    uid,
    storagePrefix: "hatchery-registry",
  };
}

async function waitForMinimumSplashDuration(
  startedAtMs: number,
  minimumSplashDurationMs: number,
): Promise<void> {
  const remainingMs = minimumSplashDurationMs - (Date.now() - startedAtMs);
  if (remainingMs <= 0) {
    return;
  }
  await new Promise((resolve) => window.setTimeout(resolve, remainingMs));
}
